import type { Goal } from './types'

/**
 * Pure goal-progress calc, mirroring iOS `Goal.progress(currentAmount:)`.
 *
 * `currentAmount` semantics differ by type:
 *   - target_by_date / monthly_contribution → category.available
 *     (how much they've put away so far)
 *   - spend_target → |category.activity|
 *     (how much they've spent — capped against the target)
 *
 * Returns percentage (0..100, capped) + a human-readable status
 * string. Callers can render the percentage as a progress bar and
 * the status under the row.
 */

export interface GoalProgress {
  percentage: number
  description: string
  achieved: boolean
}

export function goalProgress(goal: Goal, currentAmount: number): GoalProgress {
  switch (goal.type) {
    case 'target_by_date': {
      const target = goal.targetAmount ?? 0
      if (target <= 0 || !goal.targetDate) {
        return { percentage: 0, description: 'Invalid goal setup', achieved: false }
      }
      const percentage = Math.min(100, (currentAmount / target) * 100)
      const remaining = Math.max(0, target - currentAmount)
      const daysRemaining = daysUntil(goal.targetDate)
      if (daysRemaining <= 0) {
        return {
          percentage,
          description:
            percentage >= 100 ? 'Goal achieved!' : `Goal overdue by ${Math.abs(daysRemaining)} days`,
          achieved: percentage >= 100,
        }
      }
      const monthsRemaining = Math.max(1, daysRemaining / 30)
      const monthlyNeeded = remaining / monthsRemaining
      return {
        percentage,
        description:
          percentage >= 100
            ? 'Goal achieved!'
            : `${formatCurrency(monthlyNeeded)}/month to reach goal`,
        achieved: percentage >= 100,
      }
    }
    case 'monthly_contribution': {
      const contribution = goal.monthlyContribution ?? 0
      if (contribution <= 0) {
        return { percentage: 0, description: 'No monthly contribution set', achieved: false }
      }
      const percentage = Math.min(100, (currentAmount / contribution) * 100)
      return {
        percentage,
        description:
          percentage >= 100
            ? 'Monthly goal achieved!'
            : `${formatCurrency(contribution - currentAmount)} more this month`,
        achieved: percentage >= 100,
      }
    }
    case 'spend_target': {
      const target = goal.targetAmount ?? 0
      if (target <= 0) {
        return { percentage: 0, description: 'No spend target set', achieved: false }
      }
      const spent = Math.abs(currentAmount)
      const percentage = Math.min(100, (spent / target) * 100)
      const remaining = Math.max(0, target - spent)
      return {
        percentage,
        description:
          percentage >= 100
            ? `Over by ${formatCurrency(spent - target)}`
            : `${formatCurrency(remaining)} left to spend`,
        achieved: spent <= target,
      }
    }
  }
}

function daysUntil(iso: string): number {
  const target = new Date(iso)
  if (Number.isNaN(target.getTime())) return 0
  const now = new Date()
  const ms = target.getTime() - now.getTime()
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
function formatCurrency(n: number): string {
  return usd.format(n)
}
